import { Typography } from '@mui/material'
import { FC, ReactNode, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { AppTypography } from '../typography/AppTypography'
import { AppBox } from '../box/AppBox'

interface Props {
  onFileSelect: (files: File[]) => void
  acceptedFileTypes: { [key: string]: string[] }
  heading?: ReactNode
  hideDragMessage?: boolean
}

export const AppDropZone: FC<Props> = ({ onFileSelect, acceptedFileTypes, heading, hideDragMessage }) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]) // Local state to hold accepted files

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, fileRejections } = useDropzone({
    accept: acceptedFileTypes, // Dynamically accept file types
    onDrop: (acceptedFiles) => {
      setAcceptedFiles(acceptedFiles) // Update local state with accepted files
      onFileSelect(acceptedFiles) // Call parent function
    },
  })

  const acceptedFileItems = acceptedFiles.map((file) => (
    <li key={file.name}>
      {file.name} - {file.size} bytes
    </li>
  ))

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.name}>
      {file.name} - {file.size} bytes
      <ul>
        {errors.map((e) => (
          <li key={e.code}>{e.message}</li>
        ))}
      </ul>
    </li>
  ))

  return (
    <AppBox>
      <AppBox
        {...getRootProps({ className: 'dropzone' })}
        border={'3px  dotted'}
        borderColor={acceptedFiles.length > 0 ? 'primary.200' : 'grey.300'}
        minHeight={150}
        alignContent={'center'}
        textAlign={'center'}
      >
        <input {...getInputProps()} />
        {acceptedFiles.length > 0 ? (
          <AppTypography color={'primary.200'} fontSize={14}>
            {acceptedFiles[0].name}
          </AppTypography>
        ) : (
          <>
            {heading ?? (
              <AppTypography color={'grey.500'} fontSize={14}>
                Drop files here
              </AppTypography>
            )}
          </>
        )}
      </AppBox>
      {isDragAccept && <AppTypography>All files will be accepted</AppTypography>}
      {isDragReject && <AppTypography color={'red'}>This file format is not accepted.</AppTypography>}
      {!hideDragMessage && !isDragActive && <AppTypography>Drop some files here ...</AppTypography>}
      {fileRejections.map(({ errors }) => (
        <>
          {errors.map((e) => (
            <Typography key={e.code} color={'red'}>
              {e.message}
            </Typography>
          ))}
        </>
      ))}
    </AppBox>
  )
}
